@file:Suppress("MagicNumber")

package com.nonopn.skydome.common.app.pages.keraunic

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skydome.common.app.pages.pdf.KeraunicRecommendationPriority
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportData
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportLogic
import com.nonopn.skydome.common.app.pages.pdf.KeraunicReportText
import com.nonopn.skydome.common.app.pages.pdf.KeraunicRiskLevel
import com.nonopn.skydome.common.app.pages.pdf.PdfKeraunicBuilder
import com.nonopn.skydome.common.app.utils.fmt
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

// On-screen rendering of PDF pages 11 (recommendations) and 12 (methodology), same data and texts as the PDF

private val DarkRed = Color(0xFF8B0000)
private val GaugeGreen = Color(0xFF4CAF50)

private fun riskColor(level: KeraunicRiskLevel) = when (level) {
    KeraunicRiskLevel.HIGH -> ReportColors.Red
    KeraunicRiskLevel.MODERATE -> ReportColors.Amber
    KeraunicRiskLevel.LOW -> GaugeGreen
}

// ─── Page 11 — risk score and action plan ──────────────────────────────────

@Composable
internal fun KeraunicRecommendationsSection(data: KeraunicReportData, text: KeraunicReportText) {
    val r = text.recommendations
    val color = riskColor(data.risk.level)
    ReportCard {
        ReportPageHeader(text.plain(r.header))
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val gauge = @Composable { RiskGauge(data.risk.score, color, text) }
            val label = @Composable {
                Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                    Text(riskLabel(text, data.risk.level).uppercase(), fontSize = 20.sp, fontWeight = FontWeight.Black, color = color)
                    ReportBody(
                        text.plain(text.fill(r.scoreDescription, data.zone1.totalCount.toString(), data.maxKa.fmt(0), data.nsg.fmt(2)))
                    )
                }
            }
            if (maxWidth < 480.dp) {
                Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    gauge()
                    label()
                }
            } else {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                    gauge()
                    label()
                }
            }
        }
        ReportSectionTitle("\uD83D\uDD27 ${text.plain(r.actionPlan)}")
        // Same list as the PDF; the screen has no questionnaire, like the PDF export
        KeraunicReportLogic.recommendations(data.zone1.totalCount, hasRod = false, hasSurge = false).forEach { reco ->
            val (title, description) = PdfKeraunicBuilder.recommendationText(
                text, reco.kind, data.zone1.totalCount, data.zone2.totalCount, data.maxKa
            )
            RecommendationCard(reco.priority, text.plain(title), text.plain(description), text)
        }
    }
}

@Composable
private fun RiskGauge(score: Int, color: Color, text: KeraunicReportText) {
    Box(Modifier.width(220.dp).height(120.dp), contentAlignment = Alignment.BottomCenter) {
        Canvas(Modifier.width(220.dp).height(120.dp)) {
            val stroke = 12.dp.toPx()
            val radius = size.width * 0.31f
            val center = Offset(size.width / 2, size.height * 0.80f)
            val topLeft = Offset(center.x - radius, center.y - radius)
            val arcSize = Size(radius * 2, radius * 2)
            // Same segments as the PDF gauge: low, moderate, high, critical
            listOf(GaugeGreen to (180f to 60f), ReportColors.Amber to (240f to 60f), ReportColors.Red to (300f to 40f), DarkRed to (340f to 20f))
                .forEach { (c, a) ->
                    drawArc(c, a.first, a.second, useCenter = false, topLeft = topLeft, size = arcSize, style = Stroke(stroke, cap = StrokeCap.Butt))
                }
            val angle = KeraunicReportLogic.gaugeNeedleDegrees(score) * PI / 180
            val needle = radius * 0.83f
            drawLine(
                ReportColors.Text, center,
                Offset(center.x + (needle * cos(angle)).toFloat(), center.y + (needle * sin(angle)).toFloat()),
                strokeWidth = 3.dp.toPx(), cap = StrokeCap.Round
            )
            drawCircle(ReportColors.Text, radius = 5.dp.toPx(), center = center)
        }
        Column(Modifier.padding(bottom = 38.dp), horizontalAlignment = Alignment.CenterHorizontally) {
            Text("$score", fontSize = 22.sp, fontWeight = FontWeight.Black, color = color)
            Text("/100", fontSize = 9.sp, color = ReportColors.Muted)
        }
        Row(Modifier.fillMaxWidth().padding(horizontal = 12.dp), horizontalArrangement = Arrangement.SpaceBetween) {
            Text(text.plain(text.recommendations.gaugeLow), fontSize = 9.sp, fontWeight = FontWeight.Bold, color = GaugeGreen)
            Text(text.plain(text.recommendations.gaugeCritical), fontSize = 9.sp, fontWeight = FontWeight.Bold, color = DarkRed)
        }
    }
}

@Composable
private fun RecommendationCard(priority: KeraunicRecommendationPriority, title: String, description: String, text: KeraunicReportText) {
    val r = text.recommendations
    val (accent, background, label) = when (priority) {
        KeraunicRecommendationPriority.CRITICAL -> Triple(ReportColors.Red, Color(0xFFFFF3F3), r.priority1)
        KeraunicRecommendationPriority.IMPORTANT -> Triple(ReportColors.Amber, Color(0xFFFFF8E8), r.priority2)
        KeraunicRecommendationPriority.ADVISORY -> Triple(ReportColors.Blue, ReportColors.BlueBg, r.advice)
        KeraunicRecommendationPriority.SKY -> Triple(ReportColors.Green, Color(0xFFF0FBF4), r.skySentinel)
    }
    Column(
        Modifier.fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(background)
            .border(1.5.dp, accent, RoundedCornerShape(10.dp))
            .padding(12.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text.plain(label).uppercase(),
            modifier = Modifier.clip(RoundedCornerShape(4.dp)).background(accent).padding(horizontal = 8.dp, vertical = 3.dp),
            fontSize = 10.sp,
            fontWeight = FontWeight.Bold,
            color = Color.White
        )
        Text(title, fontSize = 14.sp, fontWeight = FontWeight.Bold, color = ReportColors.Text)
        Text(description, fontSize = 13.sp, color = Color(0xFF2D3748), lineHeight = 18.sp)
    }
}

// ─── Page 12 — methodology & sources ───────────────────────────────────────

@Composable
internal fun KeraunicMethodologySection(text: KeraunicReportText) {
    val m = text.methodology
    ReportCard {
        ReportPageHeader(text.plain(m.header))
        ReportSectionTitle("\uD83D\uDCD6 ${text.plain(m.section)}")
        Column(
            Modifier.fillMaxWidth()
                .clip(RoundedCornerShape(12.dp))
                .background(ReportColors.NetworkBg)
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            LabelledParagraph(text.plain(m.networkLabel), text.plain(m.networkText))
            Text(text.plain(m.referencesLabel), fontSize = 13.sp, fontWeight = FontWeight.Bold, color = Color(0xFF3A4B6A))
            Bullet("IEC 62305-2:2024", text.plain(m.iec62305))
            Bullet("IEC 62858:2019", text.plain(m.iec62858))
            Bullet("NF C 17-102:2011", text.plain(m.nfc17102))
            LabelledParagraph(text.plain(m.coverageLabel), text.plain(m.coverageText))
            Text(text.plain(m.polarityLabel), fontSize = 13.sp, fontWeight = FontWeight.Bold, color = Color(0xFF3A4B6A))
            Text("\u2022 ${text.plain(m.negativePolarity)}", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = ReportColors.Blue, lineHeight = 19.sp)
            Text("\u2022 ${text.plain(m.positivePolarity)}", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = ReportColors.Red, lineHeight = 19.sp)
            LabelledParagraph(text.plain(m.unitsLabel), text.plain(m.unitsText))
            LabelledParagraph(text.plain(m.zonesLabel), text.plain(m.zonesText))
        }
    }
}

@Composable
private fun LabelledParagraph(label: String, body: String) {
    Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(label, fontSize = 13.sp, fontWeight = FontWeight.Bold, color = Color(0xFF3A4B6A))
        Text(body, fontSize = 13.sp, color = Color(0xFF3A4B6A), lineHeight = 19.sp)
    }
}

@Composable
private fun Bullet(norm: String, body: String) {
    Text("\u2022 $norm \u2014 $body", fontSize = 13.sp, color = Color(0xFF3A4B6A), lineHeight = 19.sp)
}
